/**
 * tiers.js — frontend mirror of /api/pricing/tiers.
 *
 * Most code paths should fetch from the API (single source of truth),
 * but a few in-flight UX flows (paywall modals, walkthrough copy) need a
 * synchronous lookup at render time without waiting on a fetch. Those
 * read from the constants below, which MUST be kept in sync with
 * backend/shared/tiers.py.
 *
 * If you update a limit on the backend, mirror it here. The
 * `validateTiersInSync` test (added in iter_165) will fail in CI if the
 * two files drift.
 */

export const TIER_ORDER = ['free', 'student', 'scholar', 'pro'];

export const TIER_RANK = Object.fromEntries(
  TIER_ORDER.map((t, i) => [t, i])
);

const UNLIMITED = Infinity;

export const TIERS = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Get started. No card needed.',
    priceUsd: 0,
    accent: 'slate',
    highlights: [
      '5 lecture recordings/month (≤30 min each)',
      'AI transcription on every recording',
      'Up to 50 flashcards',
      '5 AI Study Buddy sessions/month',
      'Manual spaced repetition',
      'Focus timer (no streak tracking)',
      '200 MB storage',
    ],
    limits: {
      lectureRecordingsPerMonth: 5,
      lectureMaxMinutes: 30,
      flashcardsTotal: 50,
      aiSessionsPerMonth: 5,
      studyPactsActive: 0,
      examMode: false,
      streakFreezesPerMonth: 0,
      marketplaceCanBuy: false,
      marketplaceListingCap: 0,
      storageMb: 200,
      lectureExport: false,
      advancedAnalytics: false,
      offlineMode: false,
      driveOnedriveSync: false,
      priorityAi: false,
      apiAccess: false,
      deviceSyncMax: 1,
    },
  },
  student: {
    id: 'student',
    name: 'Student',
    tagline: 'Built for your daily study routine.',
    priceUsd: 1.99,
    accent: 'blue',
    recommended: true,            // highlighted as best-value (Feb 2026 update)
    highlights: [
      '20 lecture recordings/month (≤60 min each)',
      'Unlimited flashcards',
      '30 AI Study Buddy sessions/month',
      'Automated SM-2 spaced repetition',
      'Streak tracking + 1 freeze/month',
      'Basic exam mode (10 questions per session)',
      '1 active study pact',
      'Weekly retro email digest',
      '1 GB storage',
      'Community marketplace buying',
    ],
    limits: {
      lectureRecordingsPerMonth: 20,
      lectureMaxMinutes: 60,
      flashcardsTotal: UNLIMITED,
      aiSessionsPerMonth: 30,
      studyPactsActive: 1,
      examMode: true,
      streakFreezesPerMonth: 1,
      marketplaceCanBuy: true,
      marketplaceListingCap: 0,
      storageMb: 1024,
      lectureExport: false,
      advancedAnalytics: false,
      offlineMode: false,
      driveOnedriveSync: false,
      priorityAi: false,
      apiAccess: false,
      deviceSyncMax: 2,
    },
  },
  scholar: {
    id: 'scholar',
    name: 'Scholar',
    tagline: 'For students who study seriously.',
    priceUsd: 4.99,
    accent: 'violet',
    highlights: [
      'Unlimited lecture recordings (≤90 min each)',
      'Unlimited AI Study Buddy sessions',
      'Full exam mode — unlimited questions, timed mock exams',
      '3 active study pacts + class network visibility',
      'Streak resurrection (1 per 90 days)',
      'Marketplace selling (up to 10 decks, 15% commission)',
      'Priority AI response speed',
      'Lecture export (PDF, DOCX)',
      '2 GB storage',
      'Push notifications (all 4 personalised triggers)',
    ],
    limits: {
      lectureRecordingsPerMonth: UNLIMITED,
      lectureMaxMinutes: 90,
      flashcardsTotal: UNLIMITED,
      aiSessionsPerMonth: UNLIMITED,
      studyPactsActive: 3,
      examMode: true,
      streakFreezesPerMonth: 3,
      marketplaceCanBuy: true,
      marketplaceListingCap: 10,
      storageMb: 2048,
      lectureExport: true,
      advancedAnalytics: false,
      offlineMode: false,
      driveOnedriveSync: false,
      priorityAi: true,
      apiAccess: false,
      deviceSyncMax: 3,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tagline: 'Your complete academic operating system.',
    priceUsd: 9.99,
    accent: 'amber',
    highlights: [
      'Unlimited everything',
      '5 GB cloud storage',
      'Unlimited marketplace listings (10% commission)',
      'Advanced analytics — mastery trends, weak topic ID, study patterns',
      'Google Drive + OneDrive sync',
      'Offline mode (download lectures + decks)',
      'Early access to new features',
      'Priority support (24h response)',
      'Multi-device sync (up to 5 devices)',
      'Shareable study profile + Wrapped semester card',
      'API access',
    ],
    limits: {
      lectureRecordingsPerMonth: UNLIMITED,
      lectureMaxMinutes: UNLIMITED,
      flashcardsTotal: UNLIMITED,
      aiSessionsPerMonth: UNLIMITED,
      studyPactsActive: UNLIMITED,
      examMode: true,
      streakFreezesPerMonth: UNLIMITED,
      marketplaceCanBuy: true,
      marketplaceListingCap: UNLIMITED,
      storageMb: 5120,
      lectureExport: true,
      advancedAnalytics: true,
      offlineMode: true,
      driveOnedriveSync: true,
      priorityAi: true,
      apiAccess: true,
      deviceSyncMax: 5,
    },
  },
};

/** True if `actual` is at least `required` in the tier order. */
export function tierMeets(actual, required) {
  return (TIER_RANK[actual] ?? 0) >= (TIER_RANK[required] ?? 0);
}

/** Format a storage cap in human terms ("500 MB", "2 GB"). */
export function formatStorage(mb) {
  if (mb === UNLIMITED || !Number.isFinite(mb)) return 'Unlimited';
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

/** Pretty-print a numeric limit ("Unlimited" when Infinity). */
export function formatLimit(value) {
  if (value === UNLIMITED || value === 'unlimited' || value === Infinity) return 'Unlimited';
  if (value === true) return 'Included';
  if (value === false) return '—';
  return String(value);
}
